'use client'

import Image from 'next/image'
import {
    useEffect,
    useState,
    type FormEvent,
    type InputHTMLAttributes,
    type ReactNode
} from 'react'

import styles from './page.module.css'

type FormKey = 'driver-application' | 'client-quote'
type YesNoValue = 'yes' | 'no' | ''
type FieldErrors = Record<string, string>

type LeadFormState = Readonly<{
    status: 'idle' | 'submitting' | 'success' | 'error'
    message: string | null
    errors: FieldErrors
}>

type LeadResponse = Readonly<{
    ok?: boolean
    error?: {
        message?: string
        issues?: readonly {
            field: string
            message: string
        }[]
    }
}>

const assetPath = '/assets/figma'

const initialState: LeadFormState = {
    status: 'idle',
    message: null,
    errors: {}
}

export function LandingForms() {
    const [isQuoteOpen, setIsQuoteOpen] = useState(false)

    useEffect(() => {
        function handleQuoteOpen() {
            setIsQuoteOpen(true)
        }

        window.addEventListener('gmax:open-quote-form', handleQuoteOpen)

        return () => {
            window.removeEventListener('gmax:open-quote-form', handleQuoteOpen)
        }
    }, [])

    return (
        <div className={styles.landingFormsRoot}>
            <DriverApplicationForm />
            <ClientQuoteDialog isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} />
        </div>
    )
}

export function QuoteRequestButton({
    children,
    variant = 'primary'
}: {
    readonly children: ReactNode
    readonly variant?: 'primary' | 'secondary'
}) {
    return (
        <button
            className={variant === 'primary' ? styles.primaryButton : styles.secondaryButton}
            type="button"
            onClick={() => {
                window.dispatchEvent(new Event('gmax:open-quote-form'))
            }}
        >
            {children}
        </button>
    )
}

function DriverApplicationForm() {
    const [state, setState] = useState<LeadFormState>(initialState)
    const [openDeckExperience, setOpenDeckExperience] = useState<YesNoValue>('')
    const [oversizeExperience, setOversizeExperience] = useState<YesNoValue>('')

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        const form = event.currentTarget
        const formData = new FormData(form)
        const fields = {
            name: getFormValue(formData, 'name'),
            phone: getFormValue(formData, 'phone'),
            drivingExperienceYears: getFormValue(formData, 'drivingExperienceYears'),
            openDeckExperience,
            oversizeExperience,
            englishLevel: getFormValue(formData, 'englishLevel'),
            driverType: getFormValue(formData, 'driverType')
        }
        const validation = validateDriverFields(fields)

        if (Object.keys(validation).length > 0) {
            setState({
                status: 'error',
                message: 'Please check the highlighted fields.',
                errors: validation
            })
            return
        }

        const result = await submitLead('driver-application', fields, setState)

        if (result) {
            form.reset()
            setOpenDeckExperience('')
            setOversizeExperience('')
        }
    }

    return (
        <form
            className={styles.leadForm}
            data-analytics-form="driver-application"
            noValidate
            onSubmit={handleSubmit}
        >
            <FormStatus state={state} />
            <TextField
                autoComplete="name"
                error={state.errors.name}
                label="First & Last Name"
                name="name"
                placeholder="First & Last Name"
                required
            />
            <TextField
                autoComplete="tel"
                error={state.errors.phone}
                inputMode="tel"
                label="Phone Number"
                name="phone"
                placeholder="+1 "
                required
            />
            <TextField
                error={state.errors.drivingExperienceYears}
                inputMode="numeric"
                label="Years of experience as a truck driver"
                min="0"
                name="drivingExperienceYears"
                icon={`${assetPath}/driver-experience-select-icon.svg`}
                placeholder="Years of experience"
                required
            />
            <SegmentedYesNo
                error={state.errors.openDeckExperience}
                label="Open deck experience"
                name="openDeckExperience"
                value={openDeckExperience}
                onChange={setOpenDeckExperience}
            />
            <SegmentedYesNo
                error={state.errors.oversizeExperience}
                label="Oversize experience"
                name="oversizeExperience"
                value={oversizeExperience}
                onChange={setOversizeExperience}
            />
            <TextField
                error={state.errors.englishLevel}
                label="English Level"
                name="englishLevel"
                placeholder="Write your English language level"
                required
            />
            <TextField
                error={state.errors.driverType}
                label="Owner-operator or Company Driver (Optional)"
                name="driverType"
                placeholder="Trucking LLC"
            />
            <button
                className={styles.primaryButton}
                disabled={state.status === 'submitting'}
                type="submit"
            >
                {state.status === 'submitting' ? 'Sending...' : 'Send Message'}
            </button>
        </form>
    )
}

function ClientQuoteDialog({
    isOpen,
    onClose
}: {
    readonly isOpen: boolean
    readonly onClose: () => void
}) {
    const [state, setState] = useState<LeadFormState>(initialState)

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        const form = event.currentTarget
        const formData = new FormData(form)
        const fields = {
            company: getFormValue(formData, 'company'),
            phone: getFormValue(formData, 'phone'),
            email: getFormValue(formData, 'email'),
            origin: getFormValue(formData, 'origin'),
            destination: getFormValue(formData, 'destination'),
            commodity: getFormValue(formData, 'commodity'),
            length: getFormValue(formData, 'length'),
            width: getFormValue(formData, 'width'),
            height: getFormValue(formData, 'height'),
            weight: getFormValue(formData, 'weight'),
            volume: getFormValue(formData, 'volume')
        }
        const validation = validateQuoteFields(fields)

        if (Object.keys(validation).length > 0) {
            setState({
                status: 'error',
                message: 'Please check the highlighted fields.',
                errors: validation
            })
            return
        }

        const result = await submitLead('client-quote', fields, setState)

        if (result) {
            form.reset()
        }
    }

    if (!isOpen) {
        return null
    }

    return (
        <div className={styles.quoteOverlay} role="presentation">
            <section
                aria-label="Request a quote"
                aria-modal="true"
                className={styles.quoteDialog}
                role="dialog"
            >
                <div className={styles.quoteDialogScroller}>
                    <form
                        className={`${styles.leadForm} ${styles.quoteForm}`}
                        data-analytics-form="client-quote"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <FormStatus state={state} />
                        <TextField
                            error={state.errors.company}
                            label="Company Name"
                            name="company"
                            placeholder="Trucking LLC"
                            required
                        />
                        <TextField
                            autoComplete="tel"
                            error={state.errors.phone}
                            inputMode="tel"
                            label="Phone Number"
                            name="phone"
                            placeholder="+1 "
                            required
                        />
                        <TextField
                            autoComplete="email"
                            error={state.errors.email}
                            inputMode="email"
                            label="Email Address"
                            name="email"
                            placeholder="yourmail@mail.com"
                            required
                            type="email"
                        />
                        <TextField
                            error={state.errors.origin}
                            label="Origin"
                            name="origin"
                            placeholder="Origin"
                            required
                        />
                        <TextField
                            error={state.errors.destination}
                            label="Destination"
                            name="destination"
                            placeholder="Destination"
                            required
                        />
                        <TextField
                            error={state.errors.commodity}
                            label="Commodity"
                            name="commodity"
                            placeholder="Commodity"
                            required
                        />
                        <div className={styles.dimensionGroup}>
                            <p>Dimensions</p>
                            <div className={styles.dimensionGrid}>
                                <TextField
                                    error={state.errors.length}
                                    icon={`${assetPath}/dimension-length.svg`}
                                    inputMode="decimal"
                                    label="Length"
                                    min="0"
                                    name="length"
                                    placeholder="+1 "
                                    required
                                />
                                <TextField
                                    error={state.errors.width}
                                    icon={`${assetPath}/dimension-width.svg`}
                                    inputMode="decimal"
                                    label="Width"
                                    min="0"
                                    name="width"
                                    placeholder="+1 "
                                    required
                                />
                                <TextField
                                    error={state.errors.height}
                                    icon={`${assetPath}/dimension-height.svg`}
                                    inputMode="decimal"
                                    label="Height"
                                    min="0"
                                    name="height"
                                    placeholder="+1 "
                                    required
                                />
                                <TextField
                                    error={state.errors.weight}
                                    icon={`${assetPath}/dimension-weight.svg`}
                                    inputMode="decimal"
                                    label="Weight"
                                    min="0"
                                    name="weight"
                                    placeholder="+1 "
                                    required
                                />
                            </div>
                        </div>
                        <TextField
                            error={state.errors.volume}
                            label="Volume"
                            name="volume"
                            placeholder="Volume"
                        />
                        <button
                            className={styles.primaryButton}
                            disabled={state.status === 'submitting'}
                            type="submit"
                        >
                            {state.status === 'submitting' ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                </div>
                <span className={styles.quoteFadeTop} aria-hidden="true" />
                <button
                    aria-label="Close quote form"
                    className={styles.closeButton}
                    type="button"
                    onClick={onClose}
                >
                    X
                </button>
            </section>
        </div>
    )
}

function TextField({
    error,
    icon,
    label,
    name,
    required = false,
    type = 'text',
    ...props
}: InputHTMLAttributes<HTMLInputElement> & {
    readonly error?: string
    readonly icon?: string
    readonly label: string
    readonly name: string
}) {
    const errorId = `${name}-error`

    return (
        <label className={styles.formField}>
            <span>{label}</span>
            <span className={styles.inputShell}>
                <input
                    aria-describedby={error ? errorId : undefined}
                    aria-invalid={error ? 'true' : 'false'}
                    className={icon === undefined ? undefined : styles.inputWithIcon}
                    name={name}
                    required={required}
                    type={type}
                    {...props}
                />
                {icon === undefined ? null : (
                    <Image
                        aria-hidden="true"
                        alt=""
                        className={styles.inputIcon}
                        height={24}
                        src={icon}
                        width={24}
                    />
                )}
            </span>
            {error ? (
                <small id={errorId} className={styles.fieldError}>
                    {error}
                </small>
            ) : null}
        </label>
    )
}

function SegmentedYesNo({
    error,
    label,
    name,
    value,
    onChange
}: {
    readonly error?: string
    readonly label: string
    readonly name: string
    readonly value: YesNoValue
    readonly onChange: (value: YesNoValue) => void
}) {
    const errorId = `${name}-error`

    return (
        <fieldset
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? 'true' : 'false'}
            className={styles.segmentedField}
        >
            <legend>{label}</legend>
            <div className={styles.segmentedControl}>
                <label>
                    <input
                        checked={value === 'yes'}
                        name={name}
                        type="radio"
                        value="yes"
                        onChange={() => onChange('yes')}
                    />
                    <span>Yes</span>
                </label>
                <label>
                    <input
                        checked={value === 'no'}
                        name={name}
                        type="radio"
                        value="no"
                        onChange={() => onChange('no')}
                    />
                    <span>No</span>
                </label>
            </div>
            {error ? (
                <small id={errorId} className={styles.fieldError}>
                    {error}
                </small>
            ) : null}
        </fieldset>
    )
}

function FormStatus({ state }: { readonly state: LeadFormState }) {
    if (state.message === null) {
        return null
    }

    return (
        <div
            className={`${styles.formStatus} ${styles[`formStatus_${state.status}`]}`}
            role="status"
        >
            {state.message}
        </div>
    )
}

async function submitLead(
    formKey: FormKey,
    fields: Record<string, string>,
    setState: (state: LeadFormState) => void
): Promise<boolean> {
    setState({ status: 'submitting', message: 'Sending your message...', errors: {} })

    try {
        const response = await fetch('/api/lead', {
            method: 'POST',
            headers: {
                'content-type': 'application/json'
            },
            body: JSON.stringify({
                formKey,
                fields,
                ...readSourceMetadata()
            })
        })
        const payload = (await response.json()) as LeadResponse

        if (!response.ok || payload.ok !== true) {
            setState({
                status: 'error',
                message:
                    payload.error?.message ??
                    'The request could not be completed right now. Please try again.',
                errors: mapIssues(payload.error?.issues)
            })
            return false
        }

        setState({
            status: 'success',
            message: 'Thank you. Your message was sent successfully.',
            errors: {}
        })
        return true
    } catch {
        setState({
            status: 'error',
            message: 'The request could not be completed right now. Please try again.',
            errors: {}
        })
        return false
    }
}

function getFormValue(formData: FormData, name: string): string {
    const value = formData.get(name)

    return typeof value === 'string' ? value.trim() : ''
}

function validateDriverFields(fields: Record<string, string>): FieldErrors {
    return {
        ...required(fields.name, 'name'),
        ...phone(fields.phone),
        ...nonNegativeNumber(fields.drivingExperienceYears, 'drivingExperienceYears'),
        ...yesNo(fields.openDeckExperience, 'openDeckExperience'),
        ...yesNo(fields.oversizeExperience, 'oversizeExperience'),
        ...required(fields.englishLevel, 'englishLevel')
    }
}

function validateQuoteFields(fields: Record<string, string>): FieldErrors {
    return {
        ...required(fields.company, 'company'),
        ...phone(fields.phone),
        ...email(fields.email),
        ...required(fields.origin, 'origin'),
        ...required(fields.destination, 'destination'),
        ...required(fields.commodity, 'commodity'),
        ...nonNegativeNumber(fields.length, 'length'),
        ...nonNegativeNumber(fields.width, 'width'),
        ...nonNegativeNumber(fields.height, 'height'),
        ...nonNegativeNumber(fields.weight, 'weight')
    }
}

function required(value: string, field: string): FieldErrors {
    return value.length === 0 ? { [field]: 'This field is required.' } : {}
}

function phone(value: string): FieldErrors {
    const digits = value.replace(/\D/g, '')

    if (value.length === 0) {
        return { phone: 'This field is required.' }
    }

    return digits.length < 7 || digits.length > 15 ? { phone: 'Enter a valid phone number.' } : {}
}

function email(value: string): FieldErrors {
    if (value.length === 0) {
        return { email: 'This field is required.' }
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? {} : { email: 'Enter a valid email address.' }
}

function nonNegativeNumber(value: string, field: string): FieldErrors {
    const number = Number(value)

    return value.length === 0 || !Number.isFinite(number) || number < 0
        ? { [field]: 'Enter a valid non-negative number.' }
        : {}
}

function yesNo(value: string, field: string): FieldErrors {
    return value === 'yes' || value === 'no' ? {} : { [field]: 'Choose yes or no.' }
}

function mapIssues(issues: readonly { field: string; message: string }[] | undefined): FieldErrors {
    if (issues === undefined) {
        return {}
    }

    return Object.fromEntries(issues.map((issue) => [issue.field, issue.message]))
}

function readSourceMetadata() {
    const params = new URLSearchParams(window.location.search)

    return {
        sourcePage: window.location.pathname,
        utmSource: params.get('utm_source'),
        utmMedium: params.get('utm_medium'),
        utmCampaign: params.get('utm_campaign'),
        referrer: document.referrer || null,
        clientLocale: navigator.language || null,
        clientUserAgent: navigator.userAgent || null
    }
}
